import { GridCellParams, GridRowModel, GridRowId, GridGroupNode, useGridApiRef } from '@enterprise/material-data-grid';
import { InvoiceItem } from '../../../../../core/models';
import { GridState, ItemEditQuery } from '../../../../../store/invoiceItem.store';
import { get, head, isEmpty } from 'lodash';
import { ScreenSizeType, useScreenSize } from '../../../../../hooks';
import { ItemsGridColumn, ItemsType, useItemGridColumns } from './useItemsGridColumns.hook';
import { useItemGridState } from './useItemsGridState.hook';
import { useAnimanaSpecificColumns } from './useAnimanaSpecificColumns.hook';
// import { isAutoGeneratedRow } from '@mui/x-data-grid/hooks/features/rows/gridRowsUtils';

export interface UseItemGridProps {
    columnsState?: GridState;
    onColumnsStateChange?: (state: GridState) => void;
    itemsType?: ItemsType;
}

export const useItemGrid = (params: UseItemGridProps) => {
    const { itemsType } = params;
    const { isUpTo } = useScreenSize();
    const apiRef = useGridApiRef();
    const { isAutoGenerated, columnNames, columnSizes, columns, groupColumn } = useItemGridColumns({ apiRef });
    const { animanaColumnNames, animanaColumnSizes, animanaColumns } = useAnimanaSpecificColumns({
        apiRef,
        itemsType,
        columnNames,
        columnSizes,
        columns,
    });
    const state = useItemGridState({ apiRef, columnSizes: animanaColumnSizes, ...params });

    const getRowId = (row: GridRowModel): string => {
        const { pimsGeneratedId, hospitalDescription, provider } = row as InvoiceItem;
        return `${pimsGeneratedId}-${hospitalDescription}-${provider.siteId}`;
    };

    const getGroupGeneratedRowNodes = (rowId?: GridRowId | null): GridGroupNode[] => {
        if (!rowId) {
            return [];
        }

        const node = apiRef.current.getRowNode(rowId) as GridGroupNode;
        const parentGroupRowNodes = getGroupGeneratedRowNodes(node?.parent);

        // console.log(isAutoGeneratedRow(node));

        if (node?.isAutoGenerated) {
            return [node, ...parentGroupRowNodes];
        }

        return [...parentGroupRowNodes];
    };

    const getQueryValue = (node: GridGroupNode): Partial<ItemEditQuery> => {
        const { id, groupingField } = node;
        const rowIds = apiRef.current?.getRowGroupChildren({ groupId: id, applyFiltering: true, applySorting: true });
        const rowId = head(rowIds);
        const invoiceItem = rowId && apiRef.current?.getRow<InvoiceItem>(rowId);

        switch (groupingField) {
            case ItemsGridColumn.Id:
                return { id: get(invoiceItem, ItemsGridColumn.Id) };
            case ItemsGridColumn.Description:
                return { description: get(invoiceItem, ItemsGridColumn.Description) };
            case ItemsGridColumn.Practice:
                return { practice: get(invoiceItem, ItemsGridColumn.Practice) };
            default:
                return {};
        }
    };

    const getGroupQuery = (rowId: GridRowId | null): ItemEditQuery | undefined => {
        const generatedRowNodes = getGroupGeneratedRowNodes(rowId);

        if (!generatedRowNodes.length) {
            return;
        }

        const query = generatedRowNodes.reduce((agg, node) => {
            return { ...agg, ...getQueryValue(node) };
        }, {} as ItemEditQuery);

        return (isEmpty(query) && undefined) || query;
    };

    const getInvoiceItemQuery = (rowId: GridRowId | null): ItemEditQuery | undefined => {
        if (!rowId) {
            return;
        }

        const invoiceItem = apiRef.current.getRow<InvoiceItem>(rowId);
        if (!invoiceItem) {
            return;
        }

        const query: ItemEditQuery = {
            id: invoiceItem[ItemsGridColumn.Id],
            description: invoiceItem[ItemsGridColumn.Description],
            isSub: Boolean(invoiceItem.itemParent?.pimsGeneratedId),
            practice: invoiceItem.provider.siteId,
        };

        return query;
    };

    const getQuery = (params: GridCellParams): ItemEditQuery | undefined => {
        const { id } = params;

        if (!isAutoGenerated(id)) {
            return getInvoiceItemQuery(id);
        }

        if (isAutoGenerated(id)) {
            return getGroupQuery(id);
        }

        return;
    };

    return {
        apiRef,
        isAutoGenerated,
        getRowId,
        getQuery,
        columnNames: animanaColumnNames,
        columns: animanaColumns,
        groupColumn,
        isTabletView: isUpTo(ScreenSizeType.md),
        state,
    };
};
